import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { DesktopAdSetupFactory } from 'tag/ts/tag/factories/ads/DesktopAdSetupFactory';
import { HTML5AdSetupFactory } from 'tag/ts/tag/factories/ads/HTML5AdSetupFactory';
import { IAdSetupFactory } from 'tag/ts/tag/interfaces/ads/IAdSetupFactory';
import { IAdSetupInfo } from 'tag/ts/tag/interfaces/ads/IAdSetupInfo';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { ITagEventVO } from 'tag/ts/tag/interfaces/events/ITagEventVO';
import { ICustomAdUiElements } from 'tag/ts/tag/interfaces/tracking/ICustomAdUiElements';
import { IAdUtilities } from 'tag/ts/tag/interfaces/utilities/IAdUtilities';
import { FWCustomParams } from 'tag/ts/tag/model/ads/freewheel/FWCustomParams';
import { FWGlobalParams } from 'tag/ts/tag/model/ads/freewheel/FWGlobalParams';
import { FWSiteParams } from 'tag/ts/tag/model/ads/freewheel/FWSiteParams';
import { FreewheelConstants } from 'tag/ts/tag/model/FreewheelConstants';
import { AdSetupDefinition } from 'tag/ts/tag/model/type/ads/AdSetupDefinition';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';
import { CustomAdUiElements } from 'tag/ts/tag/view/tracking/CustomAdUiElements';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Ad utilities
 */
export class AdUtilities extends AbstractTagClass implements IAdUtilities {
	/**
	 * @description Used to create adSetupInfo object
	 */
	#adSetupFactory: IAdSetupFactory;

	/**
	 * @description Singleton instance for custom ad UI elements tracking
	 */
	#customAdUiTracking: ICustomAdUiElements;

	/**
	 * @description CLID3 macro tokens that should remain unencoded in the FreeWheel template.
	 */
	#linearAdTrackingTokens = [
		'[AIID]',
		'[CAID]',
		'[SLAU]',
		'[ABID]',
		'[DUR]',
		'[RANDOM]'
	];

	/**
	 * @description Memoized result of the last computed adSetupInfo, cleared on
	 * ON_REQUEST_TO_CLEAR_ERROR (fires at the start of every load()) so each load
	 * recomputes fresh exactly once instead of on every consumer call.
	 */
	#cachedAdSetupInfo: IAdSetupInfo | undefined;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'AdUtilities'
			}
		);

		this.#initAdSetupFactory();
		this.#customAdUiTracking = CustomAdUiElements.getInstance();
		this.tagEventsHandler( this.#onTagEvent, [ TagEvent.ON_REQUEST_TO_CLEAR_ERROR ] );
	}

	/**
	 * @description Tag application event handler
	 * @param {ITagEventVO} event
	 */
	#onTagEvent = ( event: ITagEventVO ): void => {
		if ( event?.type === TagEvent.ON_REQUEST_TO_CLEAR_ERROR ) {
			// new load starting — force adSetupInfo() to recompute rather than serve last load's cached value
			this.#cachedAdSetupInfo = undefined;
		}
	};

	/**
	 * @description Returns singleton instance of Custom Ad UI Elements tracking manager
	 * @returns {ICustomAdUiElements}
	 */
	get customAdUiTracking(): ICustomAdUiElements {
		return this.#customAdUiTracking;
	}

	/**
	 * @description Checks if params.AD.CONTEXT is a non-empty object
	 * @returns {boolean}
	 */
	paramsContainsAdContext = (): boolean => {
		return (
			this.utils.Type.isObject( this.params?.AD?.CONTEXT )
			&&
			!this.utils.Type.isEmpty( this.params?.AD?.CONTEXT )
		);
	};

	/**
	 * @description Returns normalized affiliate call sign for eligible domestic live ad calls
	 * @returns {string | undefined}
	 */
	getCallSignId = (): string | undefined => {
		const callSignId = this.params?.AD?.callSignId;
		const isInternational = this.tag?.tagUtilities?.isInternationalContext( this.tag ) === true;
		const isLive = this.tag?.model?.contentUtilities?.isLive( this.params, this.tag?.model?.apiMetadata ) === true;

		if ( !this.utils.Type.isString( callSignId ) ) {
			return;
		}

		const normalizedCallSignId = callSignId.trim();
		if ( normalizedCallSignId.length === 0 || isInternational || !isLive ) {
			return;
		}

		return normalizedCallSignId;
	};

	#encodeLinearAdTrackValue = ( input: unknown ): string => {
		if ( this.utils.Type.isNullOrUndefined( input ) ) {
			return '';
		}
		const value = String( input );
		if ( this.utils.Type.isEmpty( value ) ) {
			return '';
		}

		// Encode while preserving CLID3 macro tokens (even when embedded in a larger string, e.g. _fw_hylda)
		let placeholderValue = value;
		this.#linearAdTrackingTokens.forEach( ( token, i ) => {
			placeholderValue = placeholderValue.split( token ).join( `__CLID3_TOKEN_${i}__` );
		} );
		let encoded = encodeURIComponent( placeholderValue );
		this.#linearAdTrackingTokens.forEach( ( token, i ) => {
			encoded = encoded.split( `__CLID3_TOKEN_${i}__` ).join( token );
		} );
		return encoded;
	};

	#buildLinearAdTrackQueryString = ( paramsObj: Record<string, unknown> = {} ): string => {
		// keep keys in URL if their value is an empty string
		return Object.keys( paramsObj )
			.filter( ( key ) => !this.utils.Type.isNullOrUndefined( paramsObj[ key ] ) )
			.map( ( key ) => `${key}=${this.#encodeLinearAdTrackValue( paramsObj[ key ] )}` )
			.join( '&' );
	};

	/**
	 * @description Returns a fully-qualified FreeWheel ad call URL template for CLID3 linear ad tracking.
	 * @returns {string}
	 */
	getLinearAdTrackUrl = (): string => {
		try {
			const constants = FreewheelConstants.getInstance();
			const scheme = constants.scheme;
			const host = constants.hostIMA;

			const linearAdSetupInfo: IAdSetupInfo = {
				sdk: { type: undefined, module: undefined },
				setup: { type: undefined, definition: AdSetupDefinition.LINEAR_AD_TRACKING }
			};

			const fwGlobalParams = new FWGlobalParams( { tag: this.tag, adSetupInfo: linearAdSetupInfo } ).adParameters || {};
			const fwSiteParams = new FWSiteParams( { tag: this.tag, adSetupInfo: linearAdSetupInfo } ).adParameters || {};
			const fwCustom = new FWCustomParams( { tag: this.tag, adSetupInfo: linearAdSetupInfo } );
			const fwCustomParams = fwCustom.adParameters || {};
			const fwSlotParams = fwCustom.adSlotParameters || {};
			const fwCompanionSlotParams = fwCustom.adCompanionSlotParameters || {};

			// Allow the embedding client to supply platform/user-specific overrides
			// NOTE: Values are expected to be "raw" (unencoded). Smart Tag will URL-encode values while preserving CLID3 macro tokens.
			const linearOverrides = this.params?.AD?.LINEAR_AD_TRACKING;

			let globalParams: Record<string, unknown> = ( fwGlobalParams as Record<string, unknown> ) || {};
			let siteCustomParams: Record<string, unknown> = { ...( fwSiteParams as Record<string, unknown> ), ...( fwCustomParams as Record<string, unknown> ) };
			let slotParams: Record<string, unknown> = ( fwSlotParams as Record<string, unknown> ) || {};
			let companionSlotParams: Record<string, unknown> = ( fwCompanionSlotParams as Record<string, unknown> ) || {};

			if ( this.utils.Type.isObject( linearOverrides?.global ) ) {
				globalParams = { ...globalParams, ...( linearOverrides.global as Record<string, unknown> ) };
			}
			// Backwards compatibility: some clients place "custom" values under "site" (and vice versa).
			// Merge both into the same group to avoid strict client-side bucketing requirements.
			if ( this.utils.Type.isObject( linearOverrides?.site ) ) {
				siteCustomParams = { ...siteCustomParams, ...( linearOverrides.site as Record<string, unknown> ) };
			}
			if ( this.utils.Type.isObject( linearOverrides?.custom ) ) {
				siteCustomParams = { ...siteCustomParams, ...( linearOverrides.custom as Record<string, unknown> ) };
			}
			if ( this.utils.Type.isObject( linearOverrides?.slot ) ) {
				slotParams = { ...slotParams, ...( linearOverrides.slot as Record<string, unknown> ) };
			}
			if ( this.utils.Type.isObject( linearOverrides?.companionSlot ) ) {
				companionSlotParams = { ...companionSlotParams, ...( linearOverrides.companionSlot as Record<string, unknown> ) };
			}

			const isInternational = this.tag?.tagUtilities?.isInternationalContext( this.tag ) === true;
			if ( isInternational ) {
				delete siteCustomParams._fw_us_privacy;
			} else {
				delete siteCustomParams._fw_gdpr;
				delete siteCustomParams._fw_gdpr_consent;
			}

			const globalQueryString = this.#buildLinearAdTrackQueryString( globalParams );
			const siteCustomQueryString = this.#buildLinearAdTrackQueryString( siteCustomParams );
			const slotQueryString = this.#buildLinearAdTrackQueryString( slotParams );
			const companionSlotQueryString = this.#buildLinearAdTrackQueryString( companionSlotParams );
			// Companion slots are scoped to the HTML5 CTV path; ignore leaked web overrides.
			const shouldAppendCompanionSlot = this.params?.SYSTEM?.embedType === EmbedType.HTML5;

			const url = shouldAppendCompanionSlot ?
				`${scheme}://${host}?${globalQueryString};${siteCustomQueryString};${slotQueryString};${companionSlotQueryString}` :
				`${scheme}://${host}?${globalQueryString};${siteCustomQueryString};${slotQueryString};`;
			this?.logger?.log( 'getLinearAdTrackUrl', 'returning CLID3 adsRequestTemplate:', url );
			return url;
		} catch ( e ) {
			this?.logger?.warn( 'getLinearAdTrackUrl', 'failed to build CLID3 adsRequestTemplate... returning empty string', e );
			return '';
		}
	};

	/**
	 * @description Returns cust_params object for IMA ad call
	 * @return {Record<string, any>}
	 */
	getIMACustParams = (): Record<string, any> => {
		const siteSessionData =
			this?.tag?.model?.siteUtilities?.siteSessionData;

		// if site session data is not undefined => merge it into cust_params
		if ( this.utils.Type.isObject( siteSessionData ) ) {
			const cust_params = {} as Record<string, any>;

			// loop over site session data
			Object.keys( siteSessionData ).forEach( ( key ) => {
				// isolate value
				const value = siteSessionData[ key ];

				// filter per key name so, we can add only cust_params that are needed
				switch ( key ) {
					case 'vid': // not needed in cust_params
					case 'ppid': // not needed in cust_params
					case 'tfcd': // not needed in cust_params
						// ignore them
						break;
					default: // required param
						// if value is not empty
						if ( this.utils.Type.isEmpty( value ) === false ) {
							// assign it to cust_params
							cust_params[ key ] = value;
						}
				}
			} );

			/**
			 * @description adding ad call URL var 'user'
			 * @see {@link https://jira.cbsi.com/browse/CBSSITE-9694}
			 */
			if (
				this.utils.Type.isString(
					this?.params?.CONTEXT?.metadata?.userProfileCategory
				) &&
				!this.utils.Type.isEmpty(
					this?.params?.CONTEXT?.metadata?.userProfileCategory
				)
			) {
				cust_params.user =
					this?.params?.CONTEXT?.metadata?.userProfileCategory;
			}

			return cust_params;
		}
	};

	/**
	 * @description Returns a complex object representing ad setup
	 * including type of SDK, Importable, AdSetup (ad setup type: csai, ssai + ad setup definition: IMA, DAI flavors).
	 * Memoized for the lifetime of the current load — see #cachedAdSetupInfo.
	 * @returns {IAdSetupInfo | undefined}
	 */
	adSetupInfo = (): IAdSetupInfo | undefined => {
		if ( !this.utils.Type.isEmpty( this.#cachedAdSetupInfo ) ) {
			return this.#cachedAdSetupInfo;
		}

		if ( this.utils?.Type?.isEmpty( this.#adSetupFactory ) ) {
			this?.logger?.warn( 'adSetupInfo', 'adSetupFactory is empty, can not get adSetupInfo... exiting' );
			return;
		}

		this.#cachedAdSetupInfo = this.#adSetupFactory.createAdSetupInfo();
		return this.#cachedAdSetupInfo;
	};

	/**
	 * @description Formats ad parameter
	 * @param {string} param
	 * @returns {string}
	 */
	formatAdParameter = ( param = '' ) => {
		return param.replaceAll( ' ', '_' ).toLowerCase();
	};

	/**
	 * @description Initialize adSetupFactory
	 */
	#initAdSetupFactory = (): void => {
		if ( this.utils.Type.isEmpty( this.params?.SYSTEM?.embedType ) ) {
			this?.logger?.warn( 'initAdSetupFactory', 'params.SYSTEM.embedType is empty, can not initialize adSetupFactory... exiting' );
			return;
		}

		switch ( this.params.SYSTEM.embedType ) {
			case EmbedType.DESKTOP:
			case EmbedType.MOBILE_WEB:
				this.#adSetupFactory = new DesktopAdSetupFactory( { tag: this.tag } );
				break;
			case EmbedType.HTML5:
				this.#adSetupFactory = new HTML5AdSetupFactory( { tag: this.tag } );
				break;
			default:
				this?.logger?.warn( 'initAdSetupFactory', 'params.SYSTEM.embedType not supported:', this.params.SYSTEM.embedType );
				break;
		}
		this?.logger?.log( 'initAdSetupFactory', 'adSetupFactory:', this.#adSetupFactory );
	};
}